import { m } from 'framer-motion';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import AppBar from '@mui/material/AppBar';
import Divider from '@mui/material/Divider';
import Toolbar from '@mui/material/Toolbar';
import ListItemButton from '@mui/material/ListItemButton';
import { alpha } from '@mui/material/styles';

import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';

import Image from 'src/components/image';
import Iconify from 'src/components/iconify';
import TextMaxLine from 'src/components/text-max-line';
import { varFade, MotionViewport } from 'src/components/animate';
import { FAQ_CATEGORIES, type FaqCategoryId } from './faqs-data';

// ----------------------------------------------------------------------

type Props = {
  active: FaqCategoryId | null;
  onSelect: (id: FaqCategoryId | null) => void;
};

export default function FaqsCategory({ active, onSelect }: Props) {
  const mdUp = useResponsive('up', 'md');
  const nav = useBoolean();

  const handleSelect = (id: FaqCategoryId) => {
    onSelect(active === id ? null : id);
    nav.onFalse();
  };

  if (!mdUp) {
    return (
      <>
        <AppBar position="absolute">
          <Toolbar>
            <Button startIcon={<Iconify icon="solar:list-bold" />} onClick={nav.onTrue}>
              Categories
            </Button>
            {active && (
              <Button
                size="small"
                onClick={() => onSelect(null)}
                startIcon={<Iconify icon="eva:close-fill" />}
                sx={{ ml: 'auto' }}
              >
                Clear
              </Button>
            )}
          </Toolbar>
          <Divider />
        </AppBar>

        <Drawer open={nav.value} onClose={nav.onFalse}>
          <Box gap={1} display="grid" gridTemplateColumns="repeat(2, 1fr)" sx={{ p: 1 }}>
            {FAQ_CATEGORIES.map((category) => (
              <CardMobile
                key={category.id}
                category={category}
                isActive={active === category.id}
                onClick={() => handleSelect(category.id)}
              />
            ))}
          </Box>
        </Drawer>
      </>
    );
  }

  return (
    <Box
      component={MotionViewport}
      gap={3}
      display="grid"
      gridTemplateColumns={{
        md: 'repeat(3, 1fr)',
        lg: 'repeat(5, 1fr)',
      }}
    >
      {FAQ_CATEGORIES.map((category) => (
        <m.div key={category.id} variants={varFade().in}>
          <CardDesktop
            category={category}
            isActive={active === category.id}
            onClick={() => handleSelect(category.id)}
          />
        </m.div>
      ))}
    </Box>
  );
}

// ----------------------------------------------------------------------

type CardProps = {
  category: { id: FaqCategoryId; label: string; icon: string };
  isActive: boolean;
  onClick: () => void;
};

function CardDesktop({ category, isActive, onClick }: CardProps) {
  const { label, icon } = category;

  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 3,
        borderRadius: 2,
        bgcolor: isActive ? (t) => alpha(t.palette.primary.main, 0.08) : 'unset',
        borderColor: isActive ? 'primary.main' : 'divider',
        borderWidth: isActive ? 2 : 1,
        cursor: 'pointer',
        textAlign: 'center',
        transition: 'background-color 0.2s, border-color 0.2s, transform 0.2s',
        '&:hover': {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
          transform: 'translateY(-2px)',
          boxShadow: (theme) => theme.customShadows?.z8 ?? theme.shadows[4],
        },
      }}
    >
      <Image
        disabledEffect
        alt={icon}
        src={icon}
        sx={{ mb: 2, width: 80, height: 80, mx: 'auto' }}
      />

      <TextMaxLine variant="subtitle2" persistent>
        {label}
      </TextMaxLine>
    </Paper>
  );
}

// ----------------------------------------------------------------------

function CardMobile({ category, isActive, onClick }: CardProps) {
  const { label, icon } = category;

  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        py: 2,
        maxWidth: 140,
        borderRadius: 1,
        textAlign: 'center',
        alignItems: 'center',
        typography: 'subtitle2',
        flexDirection: 'column',
        justifyContent: 'center',
        bgcolor: isActive
          ? (theme) => alpha(theme.palette.primary.main, 0.08)
          : 'background.neutral',
        border: '2px solid',
        borderColor: isActive ? 'primary.main' : 'transparent',
      }}
    >
      <Image alt={icon} src={icon} sx={{ width: 48, height: 48, mb: 1 }} />
      {label}
    </ListItemButton>
  );
}
